import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Windows a large in-memory array so only a growing slice is rendered — the DOM
 * stays small on first paint and grows as the user scrolls, instead of mounting
 * thousands of rows at once.
 *
 * Attach `sentinelRef` to an element at the end of the scroll container; when it
 * scrolls into view the window grows by `pageSize`. The window resets to the
 * first page whenever `resetKey` changes (e.g. search/sort/filter).
 */
export function useInfiniteWindow<T>(
  items: T[],
  { pageSize = 60, resetKey }: { pageSize?: number; resetKey?: unknown } = {},
): { visible: T[]; hasMore: boolean; sentinelRef: (node: Element | null) => void } {
  const [count, setCount] = useState(pageSize);

  // Reset to the first page when the underlying query/filter changes.
  useEffect(() => {
    setCount(pageSize);
  }, [resetKey, pageSize]);

  // Don't keep a window larger than the current dataset.
  const clamped = Math.min(count, Math.max(items.length, pageSize));
  const hasMore = items.length > clamped;

  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  // Callback ref: (re)observe whichever sentinel node React hands us.
  const sentinelRef = useCallback((node: Element | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && hasMoreRef.current) {
          setCount((c) => c + pageSize);
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    observerRef.current = observer;
  }, [pageSize]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { visible: items.slice(0, clamped), hasMore, sentinelRef };
}
